import React, { useState } from 'react';
import { useCart } from '../CartContext';
import { useAuth } from '../AuthContext';
import toast from 'react-hot-toast';
import './CartPage.css';

const COUPONS = { FRESH10: 10, NEWUSER20: 20, ORGANIC15: 15, SAVE50: 50 };

export default function CartPage({ navigate }) {
  const { items, total, dispatch } = useCart();
  const { user } = useAuth();
  const [coupon, setCoupon] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState('');
  const [imgErrors, setImgErrors] = useState({});

  const delivery = total > 500 ? 0 : 49;
  const tax = Math.round(total * 0.05);
  const discountAmt = Math.round(total * discount / 100);
  const grandTotal = total + delivery + tax - discountAmt;
  const savings = discountAmt + (delivery === 0 ? 49 : 0);

  const applyCoupon = () => {
    const code = coupon.trim().toUpperCase();
    if (COUPONS[code]) {
      setDiscount(COUPONS[code]);
      setCouponApplied(code);
      toast.success(`🎉 Coupon "${code}" applied! ${COUPONS[code]}% off`);
    } else {
      toast.error('Invalid coupon code');
    }
  };

  const removeCoupon = () => {
    setDiscount(0); setCouponApplied(''); setCoupon('');
    toast('Coupon removed');
  };

  if (items.length === 0) return (
    <div className="page">
      <div className="container empty-cart">
        <div className="empty-cart-icon">🛒</div>
        <h2>Your cart is empty</h2>
        <p>Looks like you haven't added anything yet.<br/>Browse our fresh products!</p>
        <div className="empty-cart-btns">
          <button className="btn-primary" onClick={() => navigate('products')}>🛒 Browse Products</button>
          {!user && <button className="btn-secondary-outline" onClick={() => navigate('login')}>Sign In</button>}
        </div>
        <div className="empty-suggestions">
          <p>Popular right now:</p>
          <div className="suggestion-pills">
            {['🍎 Fresh Fruits', '🥦 Vegetables', '🥛 Dairy', '🥩 Meat & Eggs'].map(s => (
              <button key={s} onClick={() => navigate('products')}>{s}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page cart-page">
      <div className="container">
        <div className="cart-header">
          <div>
            <h1 className="page-title">🛒 Your Cart</h1>
            <p className="page-sub">{items.length} products · {items.reduce((s, i) => s + i.qty, 0)} units</p>
          </div>
          <button className="clear-cart-btn" onClick={() => { dispatch({ type: 'CLEAR_CART' }); toast('Cart cleared'); }}>
            🗑 Clear All
          </button>
        </div>

        {/* Free delivery progress */}
        {delivery > 0 && (
          <div className="delivery-progress-bar">
            <div className="dp-info">
              <span>🚚 Add <strong>₹{500 - total}</strong> more for FREE delivery!</span>
              <span>{Math.round((total / 500) * 100)}%</span>
            </div>
            <div className="dp-track">
              <div className="dp-fill" style={{ width: `${Math.min(100, (total / 500) * 100)}%` }} />
            </div>
          </div>
        )}
        {delivery === 0 && (
          <div className="free-delivery-achieved">
            🎉 You've unlocked <strong>FREE delivery!</strong>
          </div>
        )}

        <div className="cart-layout">
          {/* Items */}
          <div className="cart-items">
            {items.map(item => (
              <div className="cart-item" key={item.id}>
                <div className="cart-item-img">
                  {!imgErrors[item.id] ? (
                    <img src={item.image} alt={item.name}
                      onError={() => setImgErrors(e => ({ ...e, [item.id]: true }))} />
                  ) : (
                    <div className="cart-img-fallback" style={{ background: `${item.color}33` }}>
                      <span>{item.name[0]}</span>
                    </div>
                  )}
                </div>
                <div className="cart-item-info">
                  <p className="cart-item-category">{item.category}</p>
                  <h4 className="cart-item-name">{item.name}</h4>
                  <p className="cart-item-unit">₹{item.price} / {item.unit}</p>
                  {item.badge && <span className="cart-badge-tag">{item.badge}</span>}
                </div>
                <div className="cart-item-actions">
                  <div className="qty-control">
                    <button className="qty-btn" onClick={() => dispatch({ type: 'UPDATE_QTY', id: item.id, qty: item.qty - 1 })}>−</button>
                    <span className="qty-count">{item.qty}</span>
                    <button className="qty-btn" onClick={() => dispatch({ type: 'UPDATE_QTY', id: item.id, qty: item.qty + 1 })}>+</button>
                  </div>
                  <div className="cart-item-right">
                    <p className="item-subtotal">₹{(item.price * item.qty).toLocaleString()}</p>
                    <button className="remove-btn" onClick={() => { dispatch({ type: 'REMOVE_ITEM', id: item.id }); toast(`${item.name} removed`); }}>
                      🗑 Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="cart-summary">
            {/* Coupon */}
            <div className="coupon-card">
              <h4>🏷 Have a Coupon?</h4>
              {couponApplied ? (
                <div className="coupon-applied">
                  <span>✅ <strong>{couponApplied}</strong> — {discount}% off applied!</span>
                  <button onClick={removeCoupon}>Remove</button>
                </div>
              ) : (
                <div className="coupon-input-row">
                  <input
                    placeholder="Enter code (e.g. FRESH10)"
                    value={coupon}
                    onChange={e => setCoupon(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === 'Enter' && applyCoupon()}
                  />
                  <button onClick={applyCoupon}>Apply</button>
                </div>
              )}
              <p className="coupon-hint">Try: FRESH10 · NEWUSER20 · ORGANIC15</p>
            </div>

            {/* Order summary */}
            <div className="summary-card">
              <h3>Order Summary</h3>
              <div className="summary-rows">
                <div className="s-row">
                  <span>Subtotal ({items.reduce((s,i)=>s+i.qty,0)} items)</span>
                  <span>₹{total.toLocaleString()}</span>
                </div>
                {discount > 0 && (
                  <div className="s-row discount-row">
                    <span>Discount ({discount}%)</span>
                    <span>−₹{discountAmt}</span>
                  </div>
                )}
                <div className="s-row">
                  <span>Delivery Charges</span>
                  <span className={delivery === 0 ? 'free-label' : ''}>
                    {delivery === 0 ? '🎉 FREE' : `₹${delivery}`}
                  </span>
                </div>
                <div className="s-row">
                  <span>GST (5%)</span>
                  <span>₹{tax}</span>
                </div>
              </div>

              {savings > 0 && (
                <div className="savings-badge">
                  💰 You're saving ₹{savings} on this order!
                </div>
              )}

              <div className="summary-divider" />
              <div className="s-row s-total">
                <span>Total Amount</span>
                <span>₹{grandTotal.toLocaleString()}</span>
              </div>

              <button className="checkout-btn" onClick={() => navigate('checkout')}>
                Proceed to Checkout →
              </button>
              <button className="continue-shopping-btn" onClick={() => navigate('products')}>
                ← Continue Shopping
              </button>

              <div className="trust-badges">
                <div><span>🔒</span><p>Secure Payment</p></div>
                <div><span>🚀</span><p>30-min Delivery</p></div>
                <div><span>🌿</span><p>Fresh Guarantee</p></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
